import React, { useRef, useEffect, useState } from 'react';
import { authFetch } from '../../auth';
import NotesNetworkView3D from './NotesNetworkView3D';
import './NotesNetworkView.css';

const FILLS = ['#6366f1','#f59e0b','#10b981','#ef4444','#8b5cf6','#06b6d4','#f97316','#84cc16','#ec4899','#3b82f6'];
const ESTROKES = ['#f59e0b','#10b981','#ef4444','#8b5cf6','#06b6d4','#f97316','#84cc16'];

// ─── Dialogs ───

function LinkNodesDialog({ nodeA, nodeB, existingTags, onClose, onAddTag }) {
  const [input, setInput] = useState('');
  return (
    <div className="net-dialog-overlay" onClick={onClose}>
      <div className="net-dialog" onClick={function(e) { e.stopPropagation(); }}>
        <h4>Link: {nodeA.label} ↔ {nodeB.label}</h4>
        {existingTags.length > 0 && (
          <div className="net-dialog-existing">
            Already linked by: {existingTags.map(function(t) { return <span key={t} className="net-dialog-tag">{t}</span>; })}
          </div>
        )}
        <p>Add a tag to both notes:</p>
        <form onSubmit={function(e) { e.preventDefault(); if (input.trim()) { onAddTag(input.trim().toLowerCase()); setInput(''); } }}>
          <input autoFocus value={input} onChange={function(e) { setInput(e.target.value); }} placeholder="new tag name" />
          <button type="submit" disabled={!input.trim()}>Add & Link</button>
        </form>
      </div>
    </div>
  );
}

function EdgeEditDialog({ commonTags, nodeA, nodeB, onClose, onRenameTag, onDeleteCommonTag, onRemoveTagFromSide }) {
  const [renameIdx, setRenameIdx] = useState(-1);
  const [renameVal, setRenameVal] = useState('');

  return (
    <div className="net-dialog-overlay" onClick={onClose}>
      <div className="net-dialog net-dialog-wide" onClick={function(e) { e.stopPropagation(); }}>
        <h4>{nodeA.label} ↔ {nodeB.label}</h4>
        <p style={{marginBottom:10,fontSize:11,color:'#8892b0'}}>Each tag links the two notes. Remove a tag from one side to break the link, or from both to delete it entirely.</p>
        {commonTags.map(function(t, i) { return (
          <div key={t} className="net-edge-tag-row">
            {renameIdx === i ? (
              <form onSubmit={function(e) { e.preventDefault(); if (renameVal.trim()) onRenameTag(t, renameVal.trim().toLowerCase()); setRenameIdx(-1); }} className="net-inline-form">
                <input autoFocus value={renameVal} onChange={function(ev) { setRenameVal(ev.target.value); }} onBlur={function() { setRenameIdx(-1); }} />
              </form>
            ) : (
              <span className="net-edge-tag-name" onDoubleClick={function() { setRenameIdx(i); setRenameVal(t); }}>{t}</span>
            )}
            <span className="net-edge-btns">
              <button onClick={function() { onDeleteCommonTag(t); }} title="Remove from both notes">Remove from both</button>
              <button onClick={function() { onRemoveTagFromSide(t, 'sideA'); }} title={'Remove only from ' + nodeA.label}>Only from {nodeA.label.length > 12 ? nodeA.label.substring(0,10) + '…' : nodeA.label}</button>
              <button onClick={function() { onRemoveTagFromSide(t, 'sideB'); }} title={'Remove only from ' + nodeB.label}>Only from {nodeB.label.length > 12 ? nodeB.label.substring(0,10) + '…' : nodeB.label}</button>
            </span>
          </div>
        ); })}
        <div className="net-dialog-actions">
          <button onClick={onClose}>Done</button>
        </div>
      </div>
    </div>
  );
}

// ─── Main View ───

export default function NotesNetworkView({ notes, onOpenNote, onNewNote, onCreateLinkedNote, onClose }) {
  const canvasRef = useRef(null);
  const wrapRef = useRef(null);
  const animRef = useRef(null);
  const graphRef = useRef({ nodes: [], edges: [] });
  const [fullscreen, setFullscreen] = useState(false);
  const [hovered, setHovered] = useState(null);
  const [ttip, setTtip] = useState({ show: false, x: 0, y: 0, node: null });
  const [dialog, setDialog] = useState(null);
  const [ctrlHeld, setCtrlHeld] = useState(false);
  const [selectedNodeId, setSelectedNodeId] = useState(null);
  const [tourActive, setTourActive] = useState(false);
  const ctrlHeldRef = useRef(false);
  useEffect(function() { ctrlHeldRef.current = ctrlHeld; }, [ctrlHeld]);
  const [simulating, setSimulating] = useState(true); // free by default
  const simulatingRef = useRef(false);
  const [view3d, setView3d] = useState(false);
  useEffect(function() { simulatingRef.current = simulating; }, [simulating]);
  const [notesState, setNotesState] = useState(notes);

  useEffect(function() { setNotesState(notes); }, [notes]);

  useEffect(function() {
    var root = document.documentElement;
    if (!root) return;
    if (fullscreen) {
      root.classList.add('notes-network-fullscreen-active');
    } else {
      root.classList.remove('notes-network-fullscreen-active');
    }
    return function() {
      root.classList.remove('notes-network-fullscreen-active');
    };
  }, [fullscreen]);

  useEffect(function() {
    function syncFullscreenState() {
      var fsElement = document.fullscreenElement || document.webkitFullscreenElement;
      setFullscreen(!!fsElement && fsElement === wrapRef.current);
    }
    document.addEventListener('fullscreenchange', syncFullscreenState);
    document.addEventListener('webkitfullscreenchange', syncFullscreenState);
    return function() {
      document.removeEventListener('fullscreenchange', syncFullscreenState);
      document.removeEventListener('webkitfullscreenchange', syncFullscreenState);
    };
  }, []);

  function toggleFullscreen() {
    var wrap = wrapRef.current;
    if (!wrap) return;
    var fsElement = document.fullscreenElement || document.webkitFullscreenElement;
    if (fsElement) {
      var exit = document.exitFullscreen || document.webkitExitFullscreen;
      if (exit) {
        var exitResult = exit.call(document);
        if (exitResult && exitResult.catch) {
          exitResult.catch(function() {
            setFullscreen(false);
          });
        }
      } else {
        setFullscreen(false);
      }
      return;
    }
    var request = wrap.requestFullscreen || wrap.webkitRequestFullscreen;
    if (request) {
      var requestResult = request.call(wrap);
      if (requestResult && requestResult.then) {
        requestResult.then(function() {
          setFullscreen(true);
        }).catch(function() {
          setFullscreen(true);
        });
      } else {
        setFullscreen(true);
      }
    } else {
      setFullscreen(true);
    }
  }

  // Keyboard
  useEffect(function() {
    function down(e) { if (e.key === 'Alt') setCtrlHeld(true); }
    function up(e) { if (e.key === 'Alt') setCtrlHeld(false); }
    window.addEventListener('keydown', down); window.addEventListener('keyup', up);
    return function() { window.removeEventListener('keydown', down); window.removeEventListener('keyup', up); };
  }, []);

  // Canvas rendering
  useEffect(function() {
    if (view3d) return; // Don't init 2D canvas in 3D mode
    var canvas = canvasRef.current;
    if (!canvas) return;
    var ctx = canvas.getContext('2d');
    var wrap = wrapRef.current;
    var W = canvas.width = wrap.clientWidth;
    var H = canvas.height = wrap.clientHeight;

    // Load saved positions from localStorage
    var savedPositions = {};
    try { savedPositions = JSON.parse(localStorage.getItem('nf_network_positions') || '{}'); } catch(e) {}

    var nodes = notesState.map(function(n, i) {
      var angle = (i / Math.max(1, notesState.length)) * Math.PI * 2;
      var radius = 250 + notesState.length * 5;
      var sx = Math.cos(angle) * radius, sy = Math.sin(angle) * radius;
      // Restore saved position if available
      var saved = savedPositions[n.uid || i];
      if (saved && typeof saved.x === 'number' && typeof saved.y === 'number') { sx = saved.x; sy = saved.y; }
      return {
      id: n.uid || i, uid: n.uid, label: n.title || 'Untitled', tags: (n.tags || []).slice(),
      color: n.color || FILLS[i % FILLS.length],
      x: sx, y: sy, vx: 0, vy: 0, fx: simulatingRef.current ? null : sx, fy: simulatingRef.current ? null : sy
    }; });
    var edges = [];
    nodes.forEach(function(a, ai) {
      nodes.forEach(function(b, bi) {
        if (ai >= bi) return;
        var shared = (a.tags || []).filter(function(t) { return (b.tags || []).includes(t); });
        if (shared.length > 0) edges.push({ aId: a.id, bId: b.id, shared: shared, strength: shared.length, color: ESTROKES[ai % ESTROKES.length] });
      });
    });
    graphRef.current = { nodes: nodes, edges: edges };

    // Auto-fit: center the view on the graph
    var minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    nodes.forEach(function(n) { minX = Math.min(minX, n.x); minY = Math.min(minY, n.y); maxX = Math.max(maxX, n.x); maxY = Math.max(maxY, n.y); });
    var cx = (minX + maxX) / 2, cy = (minY + maxY) / 2;
    var bw = (maxX - minX) || 500, bh = (maxY - minY) || 400;
    var fitZ = Math.min(W / (bw + 200), H / (bh + 200), 1.5);
    var view = { x: cx, y: cy, z: fitZ };
    var dragNode = null;
    var panning = false, panSX = 0, panSY = 0, panVX = 0, panVY = 0;
    var ctrlLinkStart = null;

    function ws(wx, wy) { return { x: (wx - view.x) * view.z + W / 2, y: (wy - view.y) * view.z + H / 2 }; }
    function sw(sx, sy) { return { x: (sx - W / 2) / view.z + view.x, y: (sy - H / 2) / view.z + view.y }; }

    function physics() {
      if (!simulatingRef.current || dialog) return;
      // Gentle wobble when free — nodes stay near position with tiny random kick
      nodes.forEach(function(n) {
        if (n.fx == null) {
          n.vx += (Math.random() - 0.5) * 0.15;
          n.vy += (Math.random() - 0.5) * 0.15;
          n.vx *= 0.45;
          n.vy *= 0.45;
          n.x += n.vx;
          n.y += n.vy;
        } else {
          n.x = n.fx; n.y = n.fy; n.vx = 0; n.vy = 0;
        }
      });
    }

    function draw() {
      ctx.clearRect(0, 0, W, H);
      // Background
      var bg = ctx.createLinearGradient(0, 0, W, H);
      bg.addColorStop(0, '#0f0f1a'); bg.addColorStop(0.5, '#13141c'); bg.addColorStop(1, '#0d0d14');
      ctx.fillStyle = bg; ctx.fillRect(0, 0, W, H);

      // Grid
      ctx.strokeStyle = 'rgba(255,255,255,0.025)'; ctx.lineWidth = 0.5;
      var gs = 60 * view.z;
      for (var x = ((view.x % 60) * view.z + W/2) % gs; x < W; x += gs) { ctx.beginPath(); ctx.moveTo(x,0); ctx.lineTo(x,H); ctx.stroke(); }
      for (var y = ((view.y % 60) * view.z + H/2) % gs; y < H; y += gs) { ctx.beginPath(); ctx.moveTo(0,y); ctx.lineTo(W,y); ctx.stroke(); }

      // Edges
      edges.forEach(function(e) {
        var a = nodes.find(function(n) { return n.id === e.aId; });
        var b = nodes.find(function(n) { return n.id === e.bId; });
        if (!a || !b) return;
        var sa = ws(a.x, a.y), sb = ws(b.x, b.y);
        ctx.beginPath(); ctx.moveTo(sa.x, sa.y); ctx.lineTo(sb.x, sb.y);
        ctx.strokeStyle = e.color; ctx.globalAlpha = 0.5; ctx.lineWidth = Math.max(1.5, e.strength * 1.5); ctx.stroke(); ctx.globalAlpha = 1;
        // Tag label
        var mx = (sa.x + sb.x) / 2, my = (sa.y + sb.y) / 2;
        var elabel = e.shared.join(', ');
        ctx.font = '9px Inter'; ctx.textAlign = 'center';
        var etextW = ctx.measureText(elabel).width + 6;
        ctx.fillStyle = 'rgba(0,0,0,0.5)';
        ctx.beginPath();
        var etx = mx - etextW / 2, ety = my - 12;
        ctx.roundRect ? ctx.roundRect(etx, ety, etextW, 13, 3) : ctx.rect(etx, ety, etextW, 13);
        ctx.fill();
        ctx.fillStyle = 'rgba(255,255,255,0.6)';
        ctx.fillText(elabel, mx, my - 5);
      });

      // Ctrl link preview
      if (ctrlLinkStart) {
        var sp = ws(ctrlLinkStart.x, ctrlLinkStart.y);
        ctx.beginPath(); ctx.moveTo(sp.x, sp.y);
        ctx.strokeStyle = 'rgba(255,255,255,0.3)'; ctx.setLineDash([5,5]); ctx.lineWidth = 2; ctx.stroke(); ctx.setLineDash([]);
      }

      // Nodes
      nodes.forEach(function(n) {
        var s = ws(n.x, n.y);
        var ec = edges.filter(function(e) { return e.aId === n.id || e.bId === n.id; }).length;
        var r = Math.max(24, Math.min(50, 26 + ec * 3));
        ctx.beginPath(); ctx.arc(s.x, s.y, r, 0, Math.PI * 2);
        var grad = ctx.createRadialGradient(s.x - r*0.3, s.y - r*0.3, r*0.1, s.x, s.y, r);
        grad.addColorStop(0, 'rgba(255,255,255,0.25)'); grad.addColorStop(1, n.color);
        ctx.fillStyle = grad;
        ctx.fill();
        ctx.strokeStyle = hovered === n.id ? '#fff' : selectedNodeId === n.id ? '#4488ff' : 'rgba(255,255,255,0.25)';
        ctx.lineWidth = hovered === n.id || selectedNodeId === n.id ? 2.5 : 1; ctx.stroke();
        if (selectedNodeId === n.id) {
          ctx.beginPath(); ctx.arc(s.x, s.y, r + 4, 0, Math.PI * 2);
          ctx.strokeStyle = 'rgba(68,136,255,0.35)'; ctx.lineWidth = 2; ctx.stroke();
        }
        var label = n.label;
        ctx.font = 'bold ' + Math.max(10, Math.min(14, r * 0.36)) + 'px Inter'; ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
        var fullW = ctx.measureText(label).width;
        var overlaps = nodes.some(function(other) {
          if (other === n) return false;
          var ox = (other.x - view.x) * view.z + W/2;
          var oy = (other.y - view.y) * view.z + H/2;
          var dist = Math.hypot(ox - s.x, oy - s.y);
          return dist < fullW / 2 + 60;
        });
        if (overlaps && label.length > 10) {
          for (var trunc = Math.min(label.length, 24); trunc > 4; trunc--) {
            var t = label.substring(0, trunc) + '\u2026';
            if (ctx.measureText(t).width < fullW * 0.7) { label = t; break; }
          }
        }
        var textW = ctx.measureText(label).width + 8;
        var textH = Math.max(10, Math.min(14, r * 0.36)) + 4;
        ctx.fillStyle = 'rgba(0,0,0,0.55)';
        ctx.beginPath();
        var tx = s.x - textW / 2, ty = s.y - textH / 2;
        ctx.roundRect ? ctx.roundRect(tx, ty, textW, textH, 4) : ctx.rect(tx, ty, textW, textH);
        ctx.fill();
        ctx.fillStyle = '#fff';
        ctx.fillText(label, s.x, s.y);
      });
    }

    function findNodeAt(sx, sy) {
      return nodes.find(function(n) {
        var s = ws(n.x, n.y);
        var ec = edges.filter(function(e) { return e.aId === n.id || e.bId === n.id; }).length;
        var r = Math.max(24, Math.min(50, 26 + ec * 3));
        return Math.hypot(s.x - sx, s.y - sy) < r + 6;
      });
    }

    function findEdgeAt(sx, sy) {
      for (var i = 0; i < edges.length; i++) {
        var e = edges[i];
        var a = nodes.find(function(n) { return n.id === e.aId; });
        var b = nodes.find(function(n) { return n.id === e.bId; });
        if (!a || !b) continue;
        var sa = ws(a.x, a.y), sb = ws(b.x, b.y);
        var mx = (sa.x + sb.x) / 2, my = (sa.y + sb.y) / 2;
        if (Math.hypot(mx - sx, my - sy) < 35) return e;
      }
      return null;
    }

    function loop() { physics(); draw(); animRef.current = requestAnimationFrame(loop); }
    loop();

    var gm = function(e) { var r = canvas.getBoundingClientRect(); return { x: e.clientX - r.left, y: e.clientY - r.top }; };

    function savePositions() {
      try {
        var pos = {};
        nodes.forEach(function(n) { pos[n.uid] = { x: Math.round(n.x), y: Math.round(n.y) }; });
        localStorage.setItem('nf_network_positions', JSON.stringify(pos));
      } catch(e) {}
    }

    canvas.onmousedown = function(e) {
      var m = gm(e);
      var hit = findNodeAt(m.x, m.y);
      if (hit && ctrlHeldRef.current) { ctrlLinkStart = hit; return; }
      if (hit) {
        var w = sw(m.x, m.y);
        dragNode = { node: hit, ox: hit.x - w.x, oy: hit.y - w.y };
        hit.fx = hit.x; hit.fy = hit.y;
        canvas.style.cursor = 'grabbing';
      } else {
        panning = true; panSX = m.x; panSY = m.y; panVX = view.x; panVY = view.y;
        canvas.style.cursor = 'grabbing';
      }
    };

    canvas.onmousemove = function(e) {
      var m = gm(e);
      if (dragNode) { var w = sw(m.x, m.y); dragNode.node.fx = w.x + dragNode.ox; dragNode.node.fy = w.y + dragNode.oy; }
      else if (panning) { view.x = panVX - (m.x - panSX) / view.z; view.y = panVY - (m.y - panSY) / view.z; }
      else {
        var hit = findNodeAt(m.x, m.y);
        setHovered(hit ? hit.id : null);
        canvas.style.cursor = hit ? (ctrlHeldRef.current ? 'crosshair' : 'pointer') : 'grab';
      }
    };

    canvas.onmouseup = function(e) {
      if (ctrlLinkStart) {
        var m = gm(e);
        var target = findNodeAt(m.x, m.y);
        if (target && target.id !== ctrlLinkStart.id) {
          var a = ctrlLinkStart, b = target;
          var shared = (a.tags || []).filter(function(t) { return (b.tags || []).includes(t); });
          setDialog({ type: 'link', nodeA: a, nodeB: b, existingTags: shared,
            onAddTag: function(tag) { addTagToBoth(a, b, tag); setDialog(null); }
          });
        } else if (!target) {
          if (onCreateLinkedNote) onCreateLinkedNote(ctrlLinkStart);
        }
        ctrlLinkStart = null;
      }
      if (dragNode) { dragNode.node.fx = simulatingRef.current ? null : dragNode.node.x; dragNode.node.fy = simulatingRef.current ? null : dragNode.node.y; dragNode = null; savePositions(); }
      panning = false; canvas.style.cursor = 'grab';
      // Single click on node: select it (if didn't drag)
      if (!dragNode && e.detail === 1) {
        var m = gm(e);
        var hit = findNodeAt(m.x, m.y);
        if (hit) {
          setSelectedNodeId(hit.id);
        } else {
          setSelectedNodeId(null);
        }
      }
    };

    canvas.ondblclick = function(e) {
      var m = gm(e);
      var hitNode = findNodeAt(m.x, m.y);
      // Double-click node: open note
      if (hitNode && onOpenNote) { onOpenNote(hitNode.uid); return; }
      var hitEdge = findEdgeAt(m.x, m.y);
      if (hitEdge) {
        var a = nodes.find(function(n) { return n.id === hitEdge.aId; });
        var b = nodes.find(function(n) { return n.id === hitEdge.bId; });
        if (a && b) setDialog({
          type: 'edge', nodeA: a, nodeB: b, commonTags: hitEdge.shared.slice(),
          onRenameTag: function(o,n) { renameCommonTag(a,b,o,n); setDialog(null); },
          onDeleteCommonTag: function(t) { deleteCommonTag(a,b,t); setDialog(null); },
          onRemoveTagFromSide: function(t,s) { removeTagFromOne(a,b,t,s); setDialog(null); }
        });
        return;
      }
      // Double-click on empty background — create new note
      if (onNewNote) onNewNote();
    };

    canvas.onwheel = function(e) {
      e.preventDefault();
      var m = gm(e);
      var wx = (m.x - W/2) / view.z + view.x, wy = (m.y - H/2) / view.z + view.y;
      view.z = Math.max(0.15, Math.min(4, view.z * (e.deltaY > 0 ? 0.88 : 1.13)));
      view.x = wx - (m.x - W/2) / view.z; view.y = wy - (m.y - H/2) / view.z;
    };

    canvas.ondblclick = function(e) {
      var m = gm(e);
      var hitNode = findNodeAt(m.x, m.y);
      if (hitNode && onOpenNote) { onOpenNote(hitNode.uid); return; }
      var hitEdge = findEdgeAt(m.x, m.y);
      if (hitEdge) {
        var a = nodes.find(function(n) { return n.id === hitEdge.aId; });
        var b = nodes.find(function(n) { return n.id === hitEdge.bId; });
        if (a && b) setDialog({
          type: 'edge', nodeA: a, nodeB: b, commonTags: hitEdge.shared.slice(),
          onRenameTag: function(o,n) { renameCommonTag(a,b,o,n); setDialog(null); },
          onDeleteCommonTag: function(t) { deleteCommonTag(a,b,t); setDialog(null); },
          onRemoveTagFromSide: function(t,s) { removeTagFromOne(a,b,t,s); setDialog(null); }
        });
      }
    };

    canvas.onmouseout = function() { setHovered(null); canvas.style.cursor = 'grab'; };

    // Drag-and-drop tag pills onto nodes
    canvas.ondragover = function(e) { e.preventDefault(); e.dataTransfer.dropEffect = 'copy'; };
    canvas.ondrop = function(e) {
      e.preventDefault();
      var tag = e.dataTransfer.getData('text/plain');
      if (!tag) return;
      var m = gm(e);
      var hit = findNodeAt(m.x, m.y);
      if (hit) {
        var tags = (hit.tags || []).slice();
        if (!tags.includes(tag)) { tags.push(tag); updateNoteTags(hit.uid, tags); }
      }
    };

    var onResize = function() { W = canvas.width = wrap.clientWidth; H = canvas.height = wrap.clientHeight; };
    var resizeObs = new ResizeObserver(onResize);
    resizeObs.observe(wrap);
    return function() { if (animRef.current) cancelAnimationFrame(animRef.current); resizeObs.disconnect(); };
  }, [notesState, onOpenNote, ctrlHeld, hovered, dialog]); // eslint-disable-line

  // ─── Tag mutations ───
  function updateNoteTags(uid, tags) {
    authFetch('/api/notes/' + uid, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ tags: tags }) }).catch(function(){});
    setNotesState(function(prev) { return prev.map(function(n) { return n.uid === uid ? { ...n, tags: tags } : n; }); });
  }

  function addTagToBoth(a, b, tag) {
    var ta = (a.tags || []).slice(), tb = (b.tags || []).slice();
    if (!ta.includes(tag)) ta.push(tag);
    if (!tb.includes(tag)) tb.push(tag);
    updateNoteTags(a.uid, ta); updateNoteTags(b.uid, tb);
  }

  function renameCommonTag(a, b, oldTag, newTag) {
    var ta = (a.tags || []).slice().map(function(t) { return t === oldTag ? newTag : t; });
    var tb = (b.tags || []).slice().map(function(t) { return t === oldTag ? newTag : t; });
    updateNoteTags(a.uid, ta); updateNoteTags(b.uid, tb);
  }

  function deleteCommonTag(a, b, tag) {
    var ta = (a.tags || []).filter(function(t) { return t !== tag; });
    var tb = (b.tags || []).filter(function(t) { return t !== tag; });
    updateNoteTags(a.uid, ta); updateNoteTags(b.uid, tb);
  }

  function removeTagFromOne(a, b, tag, side) {
    var node = side === 'sideA' ? a : b;
    var tags = (node.tags || []).filter(function(t) { return t !== tag; });
    updateNoteTags(node.uid, tags);
  }

  return (
    <div className={'notes-network-wrap' + (fullscreen ? ' fullscreen' : '')} ref={wrapRef}>
      <div className="notes-network-header">
        <h3>Network — {notesState.length} notes, {graphRef.current.edges.length} links</h3>
        <div className="notes-network-header-actions">
          <button className={'notes-network-btn' + (simulating ? ' active' : '')} onClick={function() { setSimulating(function(s) { return !s; }); }} title={simulating ? 'Freeze nodes' : 'Unfreeze nodes'}>
            {simulating ? '❄ Freeze' : '☀ Float'}
          </button>
          <button className={'notes-network-btn' + (tourActive ? ' active' : '')} onClick={function() { setTourActive(function(t) { return !t; }); }} title={tourActive ? 'Stop tour' : 'Camera tour'}>
            {tourActive ? '⏹' : '🎥'}
          </button>
          <button className={'notes-network-btn' + (view3d ? ' active' : '')} onClick={function() { setView3d(function(v) { return !v; }); }} title={view3d ? '2D view' : '3D view'}>
            {view3d ? '2D' : '3D'}
          </button>
          <button className="notes-network-btn" onClick={toggleFullscreen} title={fullscreen ? 'Exit fullscreen' : 'Fullscreen'}>
            {fullscreen
              ? <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="4 14 10 14 10 20"/><polyline points="20 10 14 10 14 4"/><line x1="14" y1="10" x2="21" y2="3"/><line x1="3" y1="21" x2="10" y2="14"/></svg>
              : <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="15 3 21 3 21 9"/><polyline points="9 21 3 21 3 15"/><line x1="21" y1="3" x2="14" y2="10"/><line x1="3" y1="21" x2="10" y2="14"/></svg>
            }
          </button>
          <button className="notes-network-btn" onClick={onClose}>Grid</button>
        </div>
      </div>
      {view3d ? (
        <NotesNetworkView3D notes={notesState} onOpenNote={onOpenNote} onClose={onClose} tourActive={tourActive} />
      ) : (
        <canvas ref={canvasRef} className="notes-network-canvas" />
      )}
      <div className="notes-network-hint">Alt+drag nodes to link · Double-click link to edit · Double-click bg to create note · Scroll zoom</div>
      {ttip.show && ttip.node && (
        <div className="notes-network-tooltip" style={{ left: ttip.x + 14, top: ttip.y - 10 }}>
          <strong>{ttip.node.label}</strong>
          {ttip.node.tags.length > 0 && <div className="net-tt-tags">{ttip.node.tags.map(function(t) { return <span key={t} className="net-tt-tag">{t}</span>; })}</div>}
        </div>
      )}
      {dialog && dialog.type === 'link' && (
        <LinkNodesDialog nodeA={dialog.nodeA} nodeB={dialog.nodeB} existingTags={dialog.existingTags} onClose={function() { setDialog(null); }} onAddTag={dialog.onAddTag} />
      )}
      {dialog && dialog.type === 'edge' && (
        <EdgeEditDialog commonTags={dialog.commonTags} nodeA={dialog.nodeA} nodeB={dialog.nodeB} onClose={function() { setDialog(null); }} onRenameTag={dialog.onRenameTag} onDeleteCommonTag={dialog.onDeleteCommonTag} onRemoveTagFromSide={dialog.onRemoveTagFromSide} />
      )}
    </div>
  );
}
